import {Pipe} from "@angular/core";
import {PaginationService} from "./pagination-service";
import {IPaginationInstance} from "./pagination-service";

const LARGE_NUMBER = 999999999;

interface IPipeState {
    collection: any[];
    start: number;
    end: number;
    slice: any[];
}

@Pipe({
    name: 'paginate',
    pure: false
})
export class PaginatePipe {

    // store the values from the last time the pipe
    state: { [id: string]: IPipeState } = {};

    constructor(public service: PaginationService) {
    }

    public transform(collection: any[], config: any): any {

        // for non-array types, throw an exception
        if (!(collection instanceof Array)) {
            throw new Error(`PaginationPipe: Argument error - expected an array, got ${typeof collection}`);
        }

        let usingConfig = typeof config === 'object';
        let serverSideMode = usingConfig && config.totalItems !== undefined;
        let instance = this.createInstance(collection, config);
        let id = instance.id;
        let start: number, end: number;

        this.service.register(instance);

        if (!usingConfig && instance.totalItems !== collection.length) {
            this.service.setTotalItems(id, collection.length);
        }
        let itemsPerPage = instance.itemsPerPage;
        if (!serverSideMode && collection instanceof Array) {
            itemsPerPage = itemsPerPage || LARGE_NUMBER;
            start = (this.service.getCurrentPage(id) - 1) * itemsPerPage;
            end = start + itemsPerPage;

            let isIdentical = this.stateIsIdentical(id, collection, start, end);
            if (isIdentical) {
                return this.state[id].slice;
            } else {
                let slice = collection.slice(start, end);
                this.saveState(id, collection, slice, start, end);
                return slice;
            }
        }
        return collection;
    }

    createInstance(collection: any[], config: any): IPaginationInstance {
        let instance: IPaginationInstance;
        if (typeof config === 'string' || typeof config === 'number') {
            let id = this.service.defaultId;
            instance = {
                id: id,
                itemsPerPage: parseInt(<string>config),
                currentPage: this.service.getCurrentPage(id) || 1,
                totalItems: collection.length
            };
        } else if (typeof config === 'object') {
            instance = {
                id: config.id || this.service.defaultId,
                itemsPerPage: config.itemsPerPage || 0,
                currentPage: config.currentPage,
                totalItems: config.totalItems || collection.length
            };
        } else {
            throw new Error(`PaginatePipe: Argument must be a string, number or an object. Got ${typeof config}`);
        }
        return instance;
    }

    /**
     * To avoid returning a brand new array each time the pipe is run, we store the state of the sliced
     * array for a given id. This means that the next time the pipe is run on this collection & id, we just
     * need to check that the collection, start and end points are all identical, and if so, return the
     * last sliced array.
     */
    saveState(id: string, collection: any[], slice: any[], start: number, end: number) {
        this.state[id] = {
            collection,
            slice,
            start,
            end
        };
    }

    /**
     * For a given id, returns true if the collection, start and end values are identical.
     */
    stateIsIdentical(id: string, collection: any[], start: number, end: number): boolean {
        let state = this.state[id];
        if (!state) {
            return false;
        }

        return state.collection === collection && state.start === start && state.end === end;
    }
}